import { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import logoAteliePro from "@/assets/logo-atelie-pro.png";
import { getQuoteByCode } from "@/integrations/supabase/quotes";
import { getArquivos, ArquivoData } from "@/integrations/supabase/arquivos";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);

export default function OrcamentoPublico() {
  const { id } = useParams();
  const navigate = useNavigate();
  const code = id as string;
  const [arquivos, setArquivos] = useState<ArquivoData[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: ["quote", code],
    queryFn: () => getQuoteByCode(code),
    enabled: Boolean(code),
  });

  const quote = data?.quote;
  const items = data?.items || [];

  // Carregar arquivos quando o orçamento é carregado
  useEffect(() => {
    if (!quote?.id) return;

    const loadArquivos = async () => {
      try {
        const arquivosCarregados = await getArquivos('quotes', quote.id);
        setArquivos(arquivosCarregados);
      } catch (error) {
        console.error("Erro ao carregar arquivos:", error);
      }
    };

    loadArquivos();
  }, [quote?.id]);

  const safeQuote = useMemo(() => ({
    code: String(quote?.code || code || ""),
    customerName: String(quote?.customer_name || quote?.client || "Cliente"),
    customerPhone: String(quote?.customer_phone || ""),
    date: String(quote?.date || new Date().toISOString().split("T")[0]),
    observations: String(quote?.observations || ""),
    discount: Number((quote as any)?.discount || 0),
    discount_type: String((quote as any)?.discount_type || "fixed"),
  }), [quote, code]);

  const subtotal = items.reduce((sum, item) => {
    const quantity = Number(item.quantity || 1);
    const value = Number(item.unit_value ?? item.value ?? 0);
    return sum + quantity * value;
  }, 0);

  const discountAmount = safeQuote.discount_type === 'percentage'
    ? (subtotal * safeQuote.discount / 100)
    : safeQuote.discount;

  const total = Math.max(0, subtotal - discountAmount);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-muted-foreground">Orçamento não encontrado.</p>
          <button
            onClick={() => navigate("/orcamentos")}
            className="mt-4 px-4 py-2 border rounded"
          >
            Voltar aos Orçamentos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-start justify-between gap-6 mb-8">
          <div className="flex items-center gap-4">
            <img src={logoAteliePro} alt="Ateliê Pro" className="h-10" />
            <div>
              <h1 className="text-xl font-semibold">Orçamento {safeQuote.code}</h1>
              <p className="text-sm text-muted-foreground">Data: {new Date(safeQuote.date).toLocaleDateString("pt-BR")}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/orcamentos")}
              className="px-4 py-2 rounded border"
            >
              ← Voltar aos Orçamentos
            </button>
            <button
              onClick={() => window.open(`/orcamentos/${safeQuote.code}/impressao`, "_blank")}
              className="px-6 py-2 bg-black text-white rounded"
            >
              Imprimir
            </button>
            <button
              onClick={() => navigator.share?.({ title: `Orçamento ${safeQuote.code}`, url: window.location.href }).catch(() => {})}
              className="px-4 py-2 rounded border"
            >
              Compartilhar
            </button>
          </div>
        </div>

        <Card>
          <CardContent className="space-y-6 p-6">
            <div>
              <p className="text-xs text-muted-foreground">Cliente</p>
              <p className="text-lg font-semibold">{safeQuote.customerName}</p>
              {safeQuote.customerPhone && (
                <p className="text-sm text-muted-foreground">{safeQuote.customerPhone}</p>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-2">Item</th>
                    <th>Descrição</th>
                    <th className="text-right">Qtd</th>
                    <th className="text-right">Valor (R$)</th>
                    <th className="text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => {
                    const quantity = Number(item.quantity || 1);
                    const value = Number(item.unit_value ?? item.value ?? 0);
                    return (
                      <tr key={`${item.description}-${index}`} className="border-t">
                        <td className="py-3">{index + 1}</td>
                        <td>{item.description || "Produto personalizado"}</td>
                        <td className="text-right">{quantity}</td>
                        <td className="text-right">{formatCurrency(value)}</td>
                        <td className="text-right">{formatCurrency(quantity * value)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t">
                    <td colSpan={4} className="text-right font-semibold py-3">Subtotal</td>
                    <td className="text-right font-semibold">{formatCurrency(subtotal)}</td>
                  </tr>
                  {safeQuote.discount > 0 && (
                    <>
                      <tr>
                        <td colSpan={4} className="text-right font-semibold py-2 text-red-600">
                          Desconto ({safeQuote.discount_type === 'percentage' ? `${safeQuote.discount.toFixed(2)}%` : 'R$'})
                        </td>
                        <td className="text-right font-semibold text-red-600">
                          -{formatCurrency(discountAmount)}
                        </td>
                      </tr>
                      <tr className="border-t bg-green-50">
                        <td colSpan={4} className="text-right font-bold py-3">Valor Final</td>
                        <td className="text-right font-bold text-green-600">{formatCurrency(total)}</td>
                      </tr>
                    </>
                  )}
                  {safeQuote.discount === 0 && (
                    <tr className="border-t bg-green-50">
                      <td colSpan={4} className="text-right font-bold py-3">Total</td>
                      <td className="text-right font-bold text-green-600">{formatCurrency(total)}</td>
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>

            {arquivos.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-3">Arquivo / Arte</p>
                <div className="space-y-2">
                  {arquivos.map((arquivo) => (
                    <div key={arquivo.id} className="border rounded-lg p-3 space-y-2 bg-background">
                      <div className="flex gap-3">
                        {arquivo.mimetype.startsWith("image/") && (
                          <div className="flex-shrink-0">
                            <img
                              src={arquivo.url}
                              alt={arquivo.nome}
                              className="w-20 h-20 object-cover rounded border border-border"
                            />
                          </div>
                        )}
                        {arquivo.mimetype === "application/pdf" && (
                          <div className="flex-shrink-0 w-20 h-20 rounded border border-border bg-red-50 flex items-center justify-center">
                            <div className="text-center">
                              <p className="text-xs font-bold text-red-600">PDF</p>
                            </div>
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{arquivo.nome}</p>
                          <p className="text-xs text-blue-600 font-semibold mt-1">📍 {arquivo.posicao}</p>
                          {arquivo.descricao && (
                            <p className="text-xs text-muted-foreground mt-1">{arquivo.descricao}</p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">{arquivo.tamanho_kb}KB</p>
                        </div>
                      </div>

                      <a
                        href={arquivo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline block"
                      >
                        🔍 Ver em tela cheia
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {safeQuote.observations && (
              <div>
                <p className="text-xs text-muted-foreground">Observações</p>
                <p>{safeQuote.observations}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


